<script setup lang="ts">
  import { InputField, UiButton } from '@logistiek-zonder-papier/lzp-library'
  import { ref } from 'vue'
  import { isAxiosError } from 'axios'
  import { useLoginStore } from '@/store/login.store.ts'

  const loginStore = useLoginStore()

  const email = ref('')
  const errorMessage = ref<string | null>(null)
  const isSubmitting = ref(false)
  const success = ref(false)

  async function submit() {
    isSubmitting.value = true
    try {
      await loginStore.forgotPassword(email.value)
      success.value = true
    } catch (error) {
      if (!isAxiosError(error)) {
        throw error
      }
      errorMessage.value = error.response?.data?.message
    }
    isSubmitting.value = false
  }
</script>

<template>
  <div class="login-layout">
    <div class="login-card">
      <section class="content-layout">
        <img
          id="logo"
          class="logo"
          src="/content/img/lzp_logo.png"
          width="200"
          height="132"
          alt="LZP logo"
        />
        <div v-if="!success" class="login-form">
          <p>
            Voer het geverifieerde e-mailadres van uw gebruikersaccount in en wij sturen u een link
            om uw wachtwoord opnieuw in te stellen.
          </p>
          <InputField
            id="email"
            v-model="email"
            type="text"
            placeholder="Voer uw e-mailadres in"
            required
            @keyup.enter="submit"
          />
          <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
          <UiButton
            :disabled="isSubmitting"
            variant="prominent"
            type="submit"
            label="Verzenden"
            @click="submit"
          />
        </div>
        <div v-else class="login-form">
          <h4>Uw verzoek is verzonden</h4>
          <p>
            Bedankt voor uw verzoek om uw wachtwoord opnieuw in te stellen. We hebben een e-mail
            gestuurd naar het opgegeven e-mailadres met verdere instructies over hoe u uw wachtwoord
            opnieuw kunt instellen. Als u de e-mail niet ontvangt, controleer dan uw spam-map of
            neem contact met ons op voor hulp.
          </p>
          <UiButton
            variant="prominent"
            type="submit"
            label="Terug naar inloggen"
            @click="$router.replace('/login')"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
  .logo {
    align-self: center;
  }

  .login-layout {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 100vh;
  }

  .login-card {
    border: 1px solid var(--colours-grey-3);
    border-radius: var(--components-card-and-modal-corners);
    background-color: var(--colours-grey-1);
    display: flex;
    width: var(--layout-8-col-text-max);
    padding: 0 var(--layout-side-padding-desktop);
    flex-direction: column;
  }

  .content-layout {
    display: flex;
    padding: var(--spacing-l) 0;
    flex-direction: column;
    gap: var(--spacing-s);
  }

  .login-form {
    display: flex;
    padding: var(--spacing-m);
    flex-direction: column;
    align-items: flex-start;
    gap: var(--spacing-s);
  }

  .error {
    color: var(--colours-support-negative);
  }
</style>
